import { ForwardedRef, forwardRef } from 'react';
import { BaseReactComponent } from '../../../types';
import { ButtonProps } from '@mui/material';
import { LoadingButton } from '@mui/lab';

export interface HiButtonProps extends BaseReactComponent {
  /**
   * On button clicked callback.
   */
  onClick: ButtonProps['onClick'];
  /**
   * The color of the component. It supports those theme colors that make sense for this component.
   * @default 'primary'
   */
  color?: ButtonProps['color'];
  /**
   * The variant to use.
   * @default 'contained'
   */
  variant?: ButtonProps['variant'];
  /**
   * Element placed before the children.
   */
  startIcon?: React.ReactNode;
  /**
   * If `true`, the loading indicator is shown.
   * @default false
   */
  loading?: boolean;
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled?: boolean;
  /**
   * The size of the button.
   * @default medium
   */
  size?: ButtonProps['size'];
  /**
   * If `true`, the button will take up the full width of its container.
   * @default false
   */
  fullWidth?: boolean;
  /**
   * The content of the button.
   */
  children?: React.ReactNode | string;
  /**
   * Override Mui Button props
   * (https://mui.com/material-ui/api/button/).
   */
  ButtonProps?: ButtonProps;
}

/**
 * HiButton is the component for buttons in Console. You should use it for all your action buttons.
 *
 * - The text describing a button should be an **action verb**, **infinitive**, that **describe the action** done by the button.
 * - Its text should be on one **single line** only, try to be concise.
 * - The text should begin with a **capital letter** and be **lowercase**.
 * - If the button action can't be done, use the **disable** style.
 * - **Icons** can be added **before the button's text**. If not specified, do not use the icon version. It often lowers the comprehension as icons’ meaning can be hard to understand.
 *
 * It extends Mui's `Button` and `LoadingButton` components.
 */
const HiButton = forwardRef(
  (
    {
      children,
      color = 'primary',
      variant = 'contained',
      startIcon = undefined,
      loading = false,
      disabled = false,
      size = 'medium',
      fullWidth = false,
      onClick,
      ButtonProps,
      ...other
    }: HiButtonProps,
    ref: ForwardedRef<any>
  ) => {
    return (
      <LoadingButton
        {...other}
        {...ButtonProps}
        color={color}
        variant={variant}
        startIcon={startIcon}
        loading={loading}
        loadingPosition={startIcon ? 'start' : 'center'}
        disabled={disabled}
        size={size}
        fullWidth={fullWidth}
        onClick={onClick}
        sx={[
          {
            borderRadius: 0,
            boxShadow: 'none',
            textTransform: 'unset',
            fontSize: 15,
            ...(size === 'small' && {
              fontSize: 14,
              lineHeight: '18px',
              p: '0 4px',
              minWidth: 'unset'
            })
          },
          color === 'darkBackground' && {
            '&:disabled': {
              color: 'rgba(255,255,255,0.5) !important'
            }
          },
          color === 'light' && {
            '&:disabled': {
              color: 'info.main',
              opacity: '.5'
            }
          }
        ]}
      >
        {children}
      </LoadingButton>
    );
  }
);

HiButton.displayName = 'HiButton';

export default HiButton;
